import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ActionEnum } from '@albatrosdeveloper/ave-models-package/lib/enums/action-entity.enum';
import { EntityEnum } from '@albatrosdeveloper/ave-models-package/lib/enums/entity.enum';
import { UserFieldEntityTypeEnum } from '@albatrosdeveloper/ave-models-package/lib/enums/user-field-entity-type.enum';
import EntityFieldsCatalogAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/fields-catalog/field-catalog.entity';
import UserFieldCatalogAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/fields-catalog/user-field-catalog/user-field-catalog.entity';
import { IAPIItemResult } from 'app/common/interfaces';
import { AbstractService } from 'app/services/abstract.service';
import { environment as env } from '../../../../../../environments/environment';
import { UpdateFieldPayload } from './interfaces/field-catalog.interface';

const apiUrl = env.API_AVE_FIELD_CATALOG;
const moduleName = 'entity-fields-catalog';
const moduleNameMaster = 'user-field-catalog';

const noCacheHeaders = {
  'Cache-Control': 'no-cache',
  Pragma: 'no-cache',
};

export type EntityFieldsCatalogPath = EntityEnum | EntityEnum[] | '_all';
export type EntityFieldsCatalogAction = ActionEnum | ActionEnum[];
export type UserFieldCatalogPath = UserFieldEntityTypeEnum | UserFieldEntityTypeEnum[] | '_all';

@Injectable({
  providedIn: 'root',
})
export class CompanyFieldCatalogService extends AbstractService<
  EntityFieldsCatalogAttributes,
  EntityFieldsCatalogAttributes,
  EntityFieldsCatalogAttributes
> {
  constructor(http: HttpClient) {
    super(apiUrl, moduleName, http);
  }

  getFields(
    companyId: string,
    entity: EntityEnum,
    action: ActionEnum
  ): Observable<IAPIItemResult<EntityFieldsCatalogAttributes>>;
  getFields(
    companyId: string,
    entity: EntityFieldsCatalogPath,
    action?: EntityFieldsCatalogAction
  ): Observable<
    IAPIItemResult<EntityFieldsCatalogAttributes | EntityFieldsCatalogAttributes[]>
  >;
  getFields(
    companyId: string,
    entity: EntityFieldsCatalogPath,
    action?: EntityFieldsCatalogAction
  ): Observable<
    IAPIItemResult<EntityFieldsCatalogAttributes | EntityFieldsCatalogAttributes[]>
  > {
    const entityPath = Array.isArray(entity) ? entity.join(',') : entity;
    let params = new HttpParams();
    if (action !== undefined) {
      params = params.set('action', Array.isArray(action) ? action.join(',') : action);
    }

    const API_URL = `${apiUrl}api/${moduleName}/${companyId}/${entityPath}`;
    return this.http.get<
      IAPIItemResult<EntityFieldsCatalogAttributes | EntityFieldsCatalogAttributes[]>
    >(API_URL, { headers: noCacheHeaders, params });
  }

  /** Indexa respuestas batch por `entity:action`. */
  indexEntityFieldsConfigs(
    data: EntityFieldsCatalogAttributes | EntityFieldsCatalogAttributes[] | null | undefined
  ): Map<string, EntityFieldsCatalogAttributes> {
    if (!data) {
      return new Map();
    }
    const list = Array.isArray(data) ? data : [data];
    return new Map(list.map((c) => [`${c.entity}:${c.action}`, c]));
  }

  updateField(id: string, fieldId: string, payload: UpdateFieldPayload) {
    const API_URL = `${apiUrl}api/${moduleName}/${id}/fields?fieldId=${fieldId}`;
    return this.http.patch(API_URL, payload);
  }

  createField(payload: unknown) {
    const API_URL = `${apiUrl}api/${moduleName}`;
    return this.http.post(API_URL, payload);
  }

  saveEntityFieldsCatalog(
    payload: unknown,
    method: 'POST' | 'PATCH',
    id?: string
  ): Observable<unknown> {
    if (method === 'POST') {
      const API_URL = `${apiUrl}api/${moduleName}`;
      return this.http.post(API_URL, payload);
    }
    if (method === 'PATCH' && id) {
      const API_URL = `${apiUrl}api/${moduleName}/${id}`;
      return this.http.patch(API_URL, payload);
    }
    throw new Error('Invalid method for saveEntityFieldsCatalog');
  }

  getFieldsMaster(
    companyId: string,
    entityType: UserFieldEntityTypeEnum
  ): Observable<IAPIItemResult<UserFieldCatalogAttributes | null>>;
  getFieldsMaster(
    companyId: string,
    entityType: UserFieldCatalogPath
  ): Observable<
    IAPIItemResult<UserFieldCatalogAttributes | UserFieldCatalogAttributes[] | null>
  >;
  getFieldsMaster(
    companyId: string,
    entityType: UserFieldCatalogPath
  ): Observable<
    IAPIItemResult<UserFieldCatalogAttributes | UserFieldCatalogAttributes[] | null>
  > {
    const entityPath = Array.isArray(entityType) ? entityType.join(',') : entityType;
    const API_URL = `${apiUrl}api/${moduleNameMaster}/${companyId}/${entityPath}`;
    return this.http.get<
      IAPIItemResult<UserFieldCatalogAttributes | UserFieldCatalogAttributes[] | null>
    >(API_URL, { headers: noCacheHeaders });
  }

  /** Indexa respuestas batch por `entityType`. */
  indexUserFieldCatalogs(
    data: UserFieldCatalogAttributes | UserFieldCatalogAttributes[] | null | undefined
  ): Map<UserFieldEntityTypeEnum, UserFieldCatalogAttributes> {
    if (!data) {
      return new Map();
    }
    const list = Array.isArray(data) ? data : [data];
    return new Map(list.map((c) => [c.entityType, c]));
  }

  saveFieldsMaster(
    payload: UpdateFieldPayload,
    method: 'POST' | 'PATCH',
    id?: string
  ): Observable<unknown> {
    if (method === 'POST') {
      const API_URL = `${apiUrl}api/${moduleNameMaster}`;
      return this.http.post(API_URL, payload);
    }
    if (method === 'PATCH') {
      const API_URL = `${apiUrl}api/${moduleNameMaster}/${id}`;
      return this.http.patch(API_URL, payload);
    }
    throw new Error('Invalid method for saveFieldsMaster');
  }
}
